import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import StarIcon from '@mui/icons-material/Star';
import { Box, Chip, CircularProgress, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { downloadArtFile } from '../lib/download';
import { AppArtFile } from '../types';

const overlayButtonSx = {
  color: '#fff',
  backgroundColor: 'rgba(255,255,255,0.12)',
  '&:hover': { backgroundColor: 'rgba(255,255,255,0.24)' },
};

interface Props {
  files: AppArtFile[];
  /** Índice aberto; `null` mantém o visualizador fechado. */
  index: number | null;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

/**
 * Visualizador em tela cheia. Mockup de app em card de 220px não dá para
 * avaliar — é justamente na hora de escolher o modelo que a imagem precisa
 * aparecer grande.
 */
const Lightbox = ({ files, index, onClose, onNavigate }: Props) => {
  const notify = useNotify();
  const [downloading, setDownloading] = useState(false);
  const isOpen = index !== null && !!files[index];

  const go = useCallback((step: number) => {
    if (index === null || files.length < 2) { return; }
    // Circular: chegar na última e continuar volta para a primeira.
    onNavigate((index + step + files.length) % files.length);
  }, [files.length, index, onNavigate]);

  useEffect(() => {
    if (!isOpen) { return; }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') { go(-1); }
      if (event.key === 'ArrowRight') { go(1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, isOpen]);

  if (!isOpen) { return null; }

  const file = files[index as number];
  const preview = file.previewUrl || (file.mimeType?.startsWith('image/') ? file.fileUrl : null);

  const download = async () => {
    setDownloading(true);
    try {
      await downloadArtFile(file);
    } catch (error: any) {
      notify(error?.message || 'Não foi possível baixar o arquivo', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog
      open
      fullScreen
      onClose={onClose}
      slotProps={{ paper: { sx: { backgroundColor: 'rgba(0,0,0,0.92)' } } }}
    >
      <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ p: 2, color: '#fff', zIndex: 1 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
            {file.fileName || 'sem nome'}
          </Typography>
          {!!file.generationRound && (
            <Chip
              size="small"
              label={`Rodada ${file.generationRound}`}
              sx={{ backgroundColor: 'rgba(255,255,255,0.16)', color: '#fff' }}
            />
          )}
          {file.isPrimary && (
            <Chip size="small" color="success" icon={<StarIcon />} label="Escolhido" />
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {(index as number) + 1} / {files.length}
          </Typography>
          <Tooltip title="Baixar original">
            <span>
              <IconButton sx={overlayButtonSx} disabled={downloading} onClick={download}>
                {downloading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : <DownloadIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Fechar (Esc)">
            <IconButton sx={overlayButtonSx} onClick={onClose}><CloseIcon /></IconButton>
          </Tooltip>
        </Stack>

        <Box
          sx={{
            flexGrow: 1, minHeight: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center', px: 8, pb: 4,
          }}
          // Clicar no vazio fecha; clicar na imagem, não.
          onClick={event => { if (event.target === event.currentTarget) { onClose(); } }}
        >
          {preview ? (
            <Box
              component="img"
              src={preview}
              alt={file.fileName || ''}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.7)' }}>
              <InsertDriveFileOutlinedIcon sx={{ fontSize: 72 }} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                {(file.fileName?.split('.').pop() || 'arquivo').toUpperCase()} não abre no navegador —
                use o botão de baixar.
              </Typography>
            </Box>
          )}
        </Box>

        {files.length > 1 && (
          <>
            <IconButton
              onClick={() => go(-1)}
              sx={{ ...overlayButtonSx, position: 'absolute', left: 16, top: '50%' }}
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
            <IconButton
              onClick={() => go(1)}
              sx={{ ...overlayButtonSx, position: 'absolute', right: 16, top: '50%' }}
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default Lightbox;
